import React, { useState } from 'react';
import { HelmetProvider } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import { Header } from '../components/layout/Header';
import { Footer } from '../components/layout/Footer';
import { MobileFooterNav } from '../components/layout/MobileFooterNav';
import { PharmacistChat } from '../components/services/PharmacistChat';
import { PageHeroBreadcrumb } from '../components/layout/PageHeroBreadcrumb';
import { CartItem } from '../components/checkout/CartItem';
import { SEO } from '../components/shared/SEO';
import { MobileCheckoutSummary } from '../components/checkout/MobileCheckoutSummary';
import { OrderSummary } from '../components/checkout/OrderSummary';
import { CouponModal } from '../components/modals/CouponModal';
import { MobileHomeHeader } from '../components/MobileHomeHeader';
import { MobileInternalHeader } from '../components/layout/MobileInternalHeader';
import { LocationModal } from '../components/modals/LocationModal';
import { trackBeginCheckout } from '../lib/analytics/events';
import { ShoppingCartIcon, CalendarIcon, TruckIcon, TagIcon, ShoppingBagIcon, ArrowRightIcon, ShieldCheckIcon, AwardIcon, RefreshCwIcon, HeartIcon } from 'lucide-react';
export function CartPage() {
  const navigate = useNavigate();
  const [locationModalOpen, setLocationModalOpen] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState<{
    name: string;
  } | null>(null);
  // Cart state
  const [cartItems, setCartItems] = useState<any[]>([{
    id: 1,
    name: 'Dyson Corrale Hair Straightener - Prussian Blue/Rich Copper',
    brand: 'Dyson',
    price: 1999,
    quantity: 1,
    image: 'https://images.unsplash.com/photo-1522338242992-e1a54906a8da?w=400&h=400&fit=crop',
    type: 'single',
    isGlobal: true
  }, {
    id: 2,
    name: 'Beamarry Argan Smooth Conditioner 380 ML',
    brand: 'Beamarry',
    price: 54.12,
    originalPrice: 85.0,
    quantity: 1,
    image: 'https://images.unsplash.com/photo-1571875257727-256c39da42af?w=400&h=400&fit=crop',
    type: 'discount'
  }, {
    id: 3,
    name: 'Panadol Advance 500mg - 24 Tablets',
    brand: 'Panadol',
    price: 12.5,
    quantity: 2,
    image: 'https://images.unsplash.com/photo-1584308666744-24d5c474f2ae?w=400&h=400&fit=crop',
    type: 'prescription',
    requiresPrescription: true
  }, {
    id: 4,
    name: 'Skincare Essentials Bundle',
    brand: 'Innova Care',
    price: 245.0,
    originalPrice: 320.0,
    quantity: 1,
    image: 'https://images.unsplash.com/photo-1556228720-195a672e8a03?w=400&h=400&fit=crop',
    type: 'bundle',
    bundleItems: [{
      id: 401,
      name: 'Gentle Cleanser 200ml',
      image: 'https://images.unsplash.com/photo-1556228578-0d85b1a4d571?w=100&h=100&fit=crop'
    }, {
      id: 402,
      name: 'Hydrating Serum 30ml',
      image: 'https://images.unsplash.com/photo-1620916566398-39f1143ab7be?w=100&h=100&fit=crop'
    }, {
      id: 403,
      name: 'Moisturizing Cream 50ml',
      image: 'https://images.unsplash.com/photo-1611080626919-7cf5a9dbab5b?w=100&h=100&fit=crop'
    }]
  }, {
    id: 5,
    name: 'La Roche-Posay Anthelios Sunscreen SPF 50+',
    brand: 'La Roche-Posay',
    price: 145.0,
    quantity: 1,
    image: 'https://images.unsplash.com/photo-1598440947619-2c35fc9aa908?w=400&h=400&fit=crop',
    type: 'gift',
    giftItem: {
      id: 501,
      name: 'Travel Size Thermal Spring Water 50ml',
      image: 'https://images.unsplash.com/photo-1556229010-6c3f2c9ca5f8?w=100&h=100&fit=crop'
    }
  }, {
    id: 6,
    name: 'Muscle Recovery Combo Set',
    brand: 'Optimum Nutrition',
    price: 450.0,
    quantity: 1,
    image: 'https://images.unsplash.com/photo-1593095191071-8216adad09a7?w=400&h=400&fit=crop',
    type: 'combo',
    comboItems: [{
      id: 601,
      name: 'Whey Protein 2kg',
      image: 'https://images.unsplash.com/photo-1579722820308-d74e571900a9?w=100&h=100&fit=crop'
    }, {
      id: 602,
      name: 'BCAA Powder 300g',
      image: 'https://images.unsplash.com/photo-1593095191071-8216adad09a7?w=100&h=100&fit=crop'
    }, {
      id: 603,
      name: 'Shaker Bottle 700ml',
      image: 'https://images.unsplash.com/photo-1594498653385-d5172c532c00?w=100&h=100&fit=crop'
    }]
  }, {
    id: 7,
    name: 'Sensodyne Rapid Relief Toothpaste',
    brand: 'Sensodyne',
    price: 32.0,
    quantity: 1,
    image: 'https://images.unsplash.com/photo-1559591937-e68fb3305e5d?w=400&h=400&fit=crop',
    type: 'double'
  }]);
  // Saved for later state
  const [savedForLater, setSavedForLater] = useState([{
    id: 301,
    name: 'Hair Serum Treatment Oil',
    price: 89.99,
    image: 'https://images.unsplash.com/photo-1608248543803-ba4f8c70ae0b?w=400&h=400&fit=crop'
  }, {
    id: 302,
    name: 'Heat Protection Spray',
    price: 45.99,
    image: 'https://images.unsplash.com/photo-1629198735700-4f4791ab66b7?w=400&h=400&fit=crop'
  }]);
  // Other state
  const [orderNotes, setOrderNotes] = useState('');
  const [couponCode, setCouponCode] = useState('');
  const [couponModalOpen, setCouponModalOpen] = useState(false);
  // Calculate totals
  const subtotal = cartItems.reduce((sum, item) => sum + item.price * item.quantity, 0);
  const discount = 451.86;
  const tax = subtotal * 0.15;
  const total = subtotal - discount + tax;
  // Installment options
  const installmentOptions = [{
    provider: 'Tamara',
    installments: 12,
    amount: (total / 12).toFixed(2),
    color: 'bg-[#1a1a1a]'
  }, {
    provider: 'Tabby',
    installments: 4,
    amount: (total / 4).toFixed(2),
    color: 'bg-[#3EDDB4]'
  }, {
    provider: 'MADA',
    installments: 4,
    amount: (total / 4).toFixed(2),
    color: 'bg-[#00A8E1]'
  }];
  // Handler functions
  const moveToCart = (itemId: number) => {
    const item = savedForLater.find(i => i.id === itemId);
    if (item) {
      setCartItems([...cartItems, {
        ...item,
        quantity: 1
      }]);
      setSavedForLater(savedForLater.filter(i => i.id !== itemId));
    }
  };
  const removeSavedItem = (itemId: number) => {
    setSavedForLater(savedForLater.filter(i => i.id !== itemId));
  };
  const getEstimatedDeliveryDate = () => {
    const today = new Date();
    const deliveryDate = new Date(today.setDate(today.getDate() + 2));
    return deliveryDate.toLocaleDateString('en-US', {
      weekday: 'long',
      month: 'long',
      day: 'numeric'
    });
  };
  const applyCoupon = () => {
    console.log('Applying coupon:', couponCode);
  };
  // Recently viewed products
  const recentlyViewed = [{
    id: 201,
    name: 'Professional Hair Dryer 2000W',
    brand: 'SalonTech',
    price: 299.99,
    originalPrice: 399.99,
    discount: 25,
    rating: 4.9,
    reviewCount: 342,
    image: 'https://images.unsplash.com/photo-1522337360788-8b13dee7a37e?w=400&h=400&fit=crop'
  }, {
    id: 202,
    name: 'Keratin Hair Mask 500ml',
    brand: 'HairCare Pro',
    price: 79.99,
    originalPrice: 99.99,
    discount: 20,
    rating: 4.7,
    reviewCount: 189,
    image: 'https://images.unsplash.com/photo-1608248543803-ba4f8c70ae0b?w=400&h=400&fit=crop'
  }, {
    id: 203,
    name: 'Curl Defining Cream',
    brand: 'CurlMaster',
    price: 56.99,
    originalPrice: 56.99,
    discount: 0,
    rating: 4.8,
    reviewCount: 234,
    image: 'https://images.unsplash.com/photo-1629198735700-4f4791ab66b7?w=400&h=400&fit=crop'
  }, {
    id: 204,
    name: 'Hair Straightening Serum',
    brand: 'SmoothSilk',
    price: 42.99,
    originalPrice: 59.99,
    discount: 28,
    rating: 4.6,
    reviewCount: 167,
    image: 'https://images.unsplash.com/photo-1616394584738-fc6e612e71b9?w=400&h=400&fit=crop'
  }, {
    id: 205,
    name: 'Volume Boosting Mousse',
    brand: 'VolumePro',
    price: 47.99,
    originalPrice: 64.99,
    discount: 26,
    rating: 4.5,
    reviewCount: 198,
    image: 'https://images.unsplash.com/photo-1522338242992-e1a54906a8da?w=400&h=400&fit=crop'
  }, {
    id: 206,
    name: 'Anti-Frizz Hair Oil',
    brand: 'FrizzControl',
    price: 52.99,
    originalPrice: 72.99,
    discount: 27,
    rating: 4.8,
    reviewCount: 223,
    image: 'https://images.unsplash.com/photo-1571875257727-256c39da42af?w=400&h=400&fit=crop'
  }];
  const handleProceedToCheckout = () => {
    // Track begin_checkout event
    trackBeginCheckout(cartItems.map(item => ({
      item_id: item.id.toString(),
      item_name: item.name,
      price: item.price,
      quantity: item.quantity
    })));
    navigate('/checkout');
  };
  return <HelmetProvider data-id="element-6422" data-mp="26lot38kvjm48">
      <SEO title="Cart" description="Review your cart at Innova Pharmacy — check quantities, prices, and promotions before checkout. Online pharmacy with fast delivery across Saudi Arabia." canonical="/en/cart/" robots="noindex,noimageindex,noarchive,follow" hreflangAr="/cart/" hreflangEn="/en/cart/" hreflangDefault="/cart/" data-id="element-6423" data-mp="26l5adpnegtp5" />
      <div className="flex flex-col min-h-screen bg-gray-50" data-id="element-6424" data-mp="2pbeqm3g0ulzu">
        {/* Mobile Header - Only on mobile */}
        <div className="md:hidden fixed top-0 left-0 right-0 z-50" data-id="element-6425" data-mp="100e3uq9r96b9">
          <MobileInternalHeader title="Shopping Cart" data-id="element-6426" data-mp="v11bxr41191t" />
        </div>
        {/* Desktop Header - Only on desktop */}
        <div className="hidden md:block" data-id="element-6427" data-mp="g5i77y7mv1tt">
          <Header locationModalOpen={locationModalOpen} setLocationModalOpen={setLocationModalOpen} selectedLocation={selectedLocation} setSelectedLocation={setSelectedLocation} data-id="element-6428" data-mp="b88hsik8uq5z" />
        </div>
        <main className="flex-grow pt-0 md:pt-[var(--header-height-desktop)] pb-40 md:pb-0" data-id="element-6429" data-mp="1ceymgc0ryf8">
          <h1 className="sr-only" data-id="element-6430" data-mp="yxb0zuuj0r2l">Shopping Cart</h1>
          {/* Page Hero Breadcrumb */}
          <PageHeroBreadcrumb breadcrumbs={[{
          label: 'Home',
          href: '/'
        }, {
          label: 'Shopping Cart'
        }]} title="Shopping Cart" description="Review your items and proceed to checkout" data-id="element-6431" data-mp="tzrk0n6g206y" />
          <div className="container mx-auto px-4 py-4 md:py-6 lg:py-8" data-id="element-6432" data-mp="1cxbyr1fhvidu">
            {cartItems.length > 0 ? <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-6" data-id="element-6433" data-mp="1l79niw020ey5">
                {/* Cart Items - Main Area */}
                <div className="lg:col-span-2 space-y-4 md:space-y-6" data-id="element-6434" data-mp="1p8lcr0f0my5t">
                  {/* Free Shipping Progress Bar */}
                  <div className="bg-white rounded-lg shadow-sm p-4 md:p-6 border border-blue-100" data-id="element-6435" data-mp="1u9iuxd00zs7b">
                    <div className="flex items-center justify-between mb-2" data-id="element-6436" data-mp="1z3wiwuccyx1y">
                      <div className="flex items-center gap-2" data-id="element-6437" data-mp="2453pwr6ofs7k">
                        <TruckIcon className="w-5 h-5 text-[#1E2B55]" data-id="element-6438" data-mp="2865rheo2ggcp" />
                        <span className="font-bold text-gray-900" data-id="element-6439" data-mp="2cxxkrwylx06j">
                          Free Shipping
                        </span>
                      </div>
                      <span className="text-sm font-medium text-[#1E2B55]" data-id="element-6440" data-mp="2hvz0q90qkjds">
                        {total >= 200 ? 'Unlocked!' : `Add ${(200 - total).toFixed(2)} SAR more`}
                      </span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2.5 mb-1" data-id="element-6441" data-mp="227kkdqi3s66f">
                      <div className={`h-2.5 rounded-full ${total >= 200 ? 'bg-green-500' : 'bg-[#1E2B55]'}`} style={{
                    width: `${Math.min(total / 200 * 100, 100)}%`
                  }} data-id="element-6442" data-mp="1x8r45qbfm12k"></div>
                    </div>
                    <p className="text-xs text-gray-500 mt-2" data-id="element-6443" data-mp="2kvpj2jjwl0yd">
                      Free shipping on orders over 200 SAR
                    </p>
                  </div>

                  {/* Cart Items */}
                  <div className="bg-white rounded-lg shadow-sm p-3 md:p-4 lg:p-6" data-id="element-6444" data-mp="2uuos438kyylw">
                    <div className="flex items-center justify-between mb-4 border-b border-gray-100 pb-4" data-id="element-6445" data-mp="2pxojbkkcjhr3">
                      <h2 className="text-lg font-bold text-gray-900" data-id="element-6446" data-mp="10hyvveuqpdbc">
                        Cart Items ({cartItems.length})
                      </h2>
                    </div>
                    <div className="space-y-3 md:space-y-4" data-id="element-6447" data-mp="376qw3riucp9b">
                      {cartItems.map(item => <CartItem key={item.id} item={item} onQuantityChange={(id, quantity) => {
                    setCartItems(cartItems.map(i => i.id === id ? {
                      ...i,
                      quantity
                    } : i));
                  }} onRemove={id => {
                    setCartItems(cartItems.filter(i => i.id !== id));
                  }} onSaveForLater={id => {
                    const itemToSave = cartItems.find(i => i.id === id);
                    if (itemToSave) {
                      setSavedForLater([...savedForLater, itemToSave]);
                      setCartItems(cartItems.filter(i => i.id !== id));
                    }
                  }} data-id="element-6448" data-mp="3c4967l8lhaiw" />)}
                    </div>
                  </div>

                  {/* Saved for Later Section */}
                  {savedForLater.length > 0 && <div className="bg-white rounded-lg shadow-sm p-3 md:p-4 lg:p-6 mt-6" data-id="element-6449" data-mp="1q07qtyv83x7y">
                      <div className="flex items-center justify-between mb-4 border-b border-gray-100 pb-4" data-id="element-6450" data-mp="1l14t6zhkota0">
                        <h2 className="text-lg font-bold text-gray-900" data-id="element-6451" data-mp="1gzszoivkvnwj">
                          Saved for Later ({savedForLater.length})
                        </h2>
                      </div>
                      <div className="space-y-4" data-id="element-6452" data-mp="175j5qwqzmhjp">
                        {savedForLater.map(item => <div key={item.id} className="flex gap-4 p-4 border border-gray-200 rounded-lg hover:border-gray-300 transition-colors" data-id="element-6453" data-mp="3it60v9qbz8i5">
                            <div className="w-20 h-20 bg-gray-100 rounded-md overflow-hidden flex-shrink-0" data-id="element-6454" data-mp="lghz7mvrkr2">
                              <img src={item.image} alt={item.name} className="w-full h-full object-cover" data-id="element-6455" data-mp="301d4r6fyt767" />
                            </div>
                            <div className="flex-1 min-w-0 flex flex-col justify-between" data-id="element-6456" data-mp="3drgkdekca2mo">
                              <div data-id="element-6457" data-mp="2h9spt78r8xb9">
                                <h3 className="text-sm font-medium text-gray-900 line-clamp-2 mb-1" data-id="element-6458" data-mp="2h8rd6yc3c2rf">
                                  {item.name}
                                </h3>
                                <p className="text-sm font-bold text-[#1E2B55]" data-id="element-6459" data-mp="3ols0bn8hjb8z">
                                  {item.price.toFixed(2)} SAR
                                </p>
                              </div>
                              <div className="flex items-center gap-4 mt-2" data-id="element-6460" data-mp="25p784di5t2u">
                                <button onClick={() => moveToCart(item.id)} className="text-sm font-medium text-[#1E2B55] hover:underline flex items-center gap-1" data-id="element-6461" data-mp="3tci794wk7m5s">
                                  <ShoppingCartIcon className="w-4 h-4" data-id="element-6462" data-mp="30qh2hygoimml" />
                                  Move to Cart
                                </button>
                                <button onClick={() => removeSavedItem(item.id)} className="text-sm font-medium text-gray-500 hover:text-red-500 transition-colors" data-id="element-6463" data-mp="3alhe4xi4eku5">
                                  Remove
                                </button>
                              </div>
                            </div>
                          </div>)}
                      </div>
                    </div>}

                  {/* Coupons Section - Mobile Only (Visible in main area) */}
                  <div className="lg:hidden bg-white rounded-lg shadow-sm p-4 border border-gray-100 mt-4" data-id="element-6464" data-mp="sgzmvl144kbc">
                    <button onClick={() => setCouponModalOpen(true)} className="flex items-center justify-between w-full group" data-id="element-6465" data-mp="1g8ethpzx9nse">
                      <div className="flex items-center gap-3" data-id="element-6466" data-mp="16efw14asms6s">
                        <div className="w-10 h-10 rounded-full bg-blue-50 flex items-center justify-center" data-id="element-6467" data-mp="1bek4c71jsglx">
                          <TagIcon className="w-5 h-5 text-[#1E2B55]" data-id="element-6468" data-mp="1z4efmk67xx42" />
                        </div>
                        <div className="text-left" data-id="element-6469" data-mp="23wo92a6jlfb3">
                          <p className="font-bold text-gray-900" data-id="element-6470" data-mp="3terrn5mifs9">Coupons</p>
                          <p className="text-xs text-gray-500" data-id="element-6471" data-mp="3vhqqwor7j94">Save More</p>
                        </div>
                      </div>
                      <span className="text-sm font-bold text-[#1E2B55] hover:underline" data-id="element-6472" data-mp="3r67m47leggc8">
                        View & Apply
                      </span>
                    </button>
                  </div>
                </div>

                {/* Right Column - Desktop Only */}
                <div className="hidden lg:block lg:col-span-1" data-id="element-6473" data-mp="iqxobbojlxa9">
                  <div className="sticky top-24 space-y-6" data-id="element-6474" data-mp="irp9dskikcma">
                    <OrderSummary subtotal={subtotal} discount={discount} tax={tax} total={total} itemCount={cartItems.reduce((acc, item) => acc + item.quantity, 0)} onOpenCoupons={() => setCouponModalOpen(true)} data-id="element-6475" data-mp="dr1lvgvhudwt" />
                  </div>
                </div>
              </div> : <div className="bg-white rounded-lg shadow-sm p-8 md:p-12 text-center mb-8" data-id="element-6476" data-mp="2oewsf05lyy3x">
                <div className="w-20 h-20 bg-gray-50 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-6477" data-mp="2tc6lcps7hrwi">
                  <ShoppingBagIcon className="w-10 h-10 text-gray-400" data-id="element-6478" data-mp="2y5hke4qs14dv" />
                </div>
                <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-2" data-id="element-6479" data-mp="324qhnjl0zke6">
                  Your cart is empty
                </h2>
                <p className="text-gray-500 mb-6 max-w-md mx-auto" data-id="element-6480" data-mp="3c3pqb25sjg16">
                  Looks like you haven't added anything to your cart yet.
                  Explore our top categories and find what you need.
                </p>
                <button onClick={() => navigate('/')} className="inline-flex items-center justify-center gap-2 bg-[#1E2B55] text-white font-bold py-3 px-8 rounded-lg hover:bg-[#162041] transition-colors" data-id="element-6481" data-mp="1hqgdp1hy7eep">
                  Continue Shopping
                  <ArrowRightIcon className="w-5 h-5" data-id="element-6482" data-mp="3utwona2odb1l" />
                </button>
              </div>}

            {/* Recently Viewed (Empty State) */}
            {cartItems.length === 0 && <div className="mt-6 md:mt-8" data-id="element-6483" data-mp="8f3l1s0hbsqh">
                <div className="flex items-center justify-between mb-4 md:mb-6" data-id="element-6484" data-mp="8gwu1dykep1n">
                  <h2 className="text-lg md:text-xl lg:text-2xl font-bold" data-id="element-6485" data-mp="ddeuw2qry38p">
                    Recently Viewed
                  </h2>
                </div>
                <div className="flex overflow-x-auto md:grid md:grid-cols-3 lg:grid-cols-6 gap-3 md:gap-4 pb-4 md:pb-0 snap-x snap-mandatory hide-scrollbar -mx-4 px-4 md:mx-0 md:px-0" data-id="element-6486" data-mp="me3bn57jyhpa">
                  {recentlyViewed.map(product => <div key={product.id} className="flex-none w-[160px] md:w-auto bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden hover:shadow-md transition-shadow snap-start" data-id="element-6487" data-mp="2o7j3g5fdm2lu">
                      <div className="relative aspect-square bg-gray-50 overflow-hidden" data-id="element-6488" data-mp="2gujtz7ngyzs5">
                        <img src={product.image} alt={product.name} className="w-full h-full object-cover" data-id="element-6489" data-mp="2bv77io0ifuq3" />
                        {product.discount > 0 && <div className="absolute top-2 left-2 bg-red-500 text-white text-xs font-bold px-2 py-1 rounded-full" data-id="element-6490" data-mp="34ibf90qzmn5k">
                            -{product.discount}%
                          </div>}
                      </div>
                      <div className="p-2 md:p-3" data-id="element-6491" data-mp="2qjuzylf27obt">
                        <p className="text-xs text-gray-500 mb-1 uppercase tracking-wide" data-id="element-6492" data-mp="1j72zhvugv5k8">
                          {product.brand}
                        </p>
                        <h3 className="text-xs md:text-sm font-medium text-gray-900 line-clamp-2 mb-2 min-h-[32px] md:min-h-[40px]" data-id="element-6493" data-mp="1f3wn06grgi5b">
                          {product.name}
                        </h3>
                        <div className="flex flex-col mb-2 md:mb-3" data-id="element-6494" data-mp="1ll7xlcpwolbc">
                          {product.originalPrice && <span className="text-xs text-gray-400 line-through" data-id="element-6495" data-mp="16qti3m5jvu1f">
                              {product.originalPrice} SAR
                            </span>}
                          <span className="text-sm md:text-base font-bold text-red-600" data-id="element-6496" data-mp="xuaw6tvql6ec">
                            {product.price} SAR
                          </span>
                        </div>
                        <button className="w-full bg-[#1E2B55] hover:bg-[#fdc300] hover:text-[#1E2B55] text-white font-medium py-1.5 md:py-2 px-2 md:px-4 rounded-lg transition-colors text-xs md:text-sm" data-id="element-6497" data-mp="nw4vgflo26b2">
                          Add to Cart
                        </button>
                      </div>
                    </div>)}
                </div>
              </div>}
          </div>
        </main>
        <div className="hidden md:block" data-id="element-6498" data-mp="3rhf606rnbu31">
          <Footer data-id="element-6499" data-mp="3riq69wlvujpe" />
        </div>

        {cartItems.length > 0 && <MobileCheckoutSummary subtotal={subtotal} discount={discount} tax={tax} total={total} onCheckout={handleProceedToCheckout} data-id="element-6500" data-mp="9cbrjvfsi5py" />}

        <MobileFooterNav data-id="element-6501" data-mp="j7vmofmj50dn" />
        <div className="hidden md:block" data-id="element-6502" data-mp="j73qp3p0t0mu">
          <PharmacistChat data-id="element-6503" data-mp="eb56zten06sr" />
        </div>
        <LocationModal isOpen={locationModalOpen} onClose={() => setLocationModalOpen(false)} onSelectLocation={location => {
        setSelectedLocation(location);
        setLocationModalOpen(false);
      }} data-id="element-6504" data-mp="ea3mkybqe7yd" />
        <CouponModal isOpen={couponModalOpen} onClose={() => setCouponModalOpen(false)} onApplyCoupon={code => {
        setCouponCode(code);
        setCouponModalOpen(false);
      }} data-id="element-6505" data-mp="120zk8c2vbxdj" />
      </div>
    </HelmetProvider>;
}